<script setup lang="ts">
import { useModalManager } from "@/composables/useModalManager";
import { usePayments } from "@/composables/usePayments";
import { useAuthenticationStore } from "@/stores/useAuthenticationStore";
import { ModalEnum } from "@/types/enums";
import UnLockIcon from "@/ui/components/icons/UnLockIcon.vue";
import { ref, watch } from "vue";
import BaseModal from "./BaseModal.vue";

const authStore = useAuthenticationStore();
const { activeModal, setRedirectUrl } = useModalManager();
const { makePayment } = usePayments();
const show = ref(false);
const loading = ref(false);

const modalTitle = "NBA Playoffs Exclusive!";
const features = [
  "<b class='font-semibold text-slate-700'>Unlimited Sports Access.</b>",
  "<b class='font-semibold text-slate-700'>Every</b> Sport, <b class='font-semibold text-slate-700'>Every</b> Prediction.",
  "<b class='font-semibold text-slate-700'>Instant Picks</b>, No Waiting.",
  "Get the edge <b class='font-semibold text-slate-700'>sports books fear.</b>",
  "<b class='font-semibold text-slate-700'>Sharp bettors</b> use AI.",
];
const price = 19;
const originalPrice = 199;

watch(
  [() => activeModal.value, () => authStore.user],
  ([modal, user]) => {
    if (modal === ModalEnum.PrePaymentModal && user && !user?.has_access) {
      setRedirectUrl(window.location.pathname);
      show.value = true;
    }
  },
  { immediate: true },
);

const doPayment = () => {
  loading.value = true;
  makePayment(authStore.user);
};

const onClose = () => {
  show.value = false;
  activeModal.value = null;
};
</script>

<template>
  <BaseModal @close="onClose" v-model:visible="show" aria-label="Pre-payment modal" role="dialog">
    <template #header>
      <div class="inline-flex w-full items-start justify-between">
        <div class="justify-start self-stretch text-2xl font-bold leading-normal text-slate-700">
          {{ modalTitle }}
        </div>
      </div>
    </template>
    <div class="inline-flex w-full flex-col items-start justify-start gap-3.5">
      <div class="flex flex-col items-start justify-start gap-1.5 self-stretch">
        <div
          class="inline-flex items-start justify-start gap-2 self-stretch"
          v-for="(feature, index) in features"
          :key="index"
        >
          <div class="justify-center text-sm font-normal text-slate-700 md:text-base">✅</div>
          <div class="flex-1 justify-center">
            <span class="text-sm leading-tight text-slate-500 md:text-base" v-html="feature"></span>
          </div>
        </div>
      </div>
      <div class="justify-start self-stretch">
        <span class="text-xs font-normal leading-normal text-slate-500 md:text-sm">🏆 </span>
        <span class="text-xs font-semibold leading-normal text-slate-700 md:text-sm"
          >Trusted by 50,000+ Fans
        </span>
        <span class="text-xs font-normal leading-normal text-slate-500 md:text-sm"
          >Winning with AI Predictions!</span
        >
      </div>
      <div
        class="outline-offset-custom flex flex-col items-center justify-center gap-2 self-stretch rounded-lg bg-sky-50 p-4 outline outline-1 outline-sky-500"
      >
        <div class="inline-flex items-center justify-between self-stretch">
          <div class="justify-start text-center">
            <span class="text-sm font-normal leading-normal text-slate-700">🚀 </span>
            <span class="text-sm font-semibold leading-normal text-slate-700"
              >NBA Playoffs Special</span
            >
          </div>
          <div class="flex items-center justify-start gap-1">
            <div
              class="justify-center text-center text-sm font-semibold leading-none text-slate-700 md:text-base"
            >
              ${{ price }}
            </div>
            <div class="relative top-[-0.5px] justify-start text-center">
              <span class="text-sm font-normal leading-none text-red-600 line-through"
                >${{ originalPrice }}</span
              >
            </div>
          </div>
        </div>
        <div class="flex w-full flex-col items-start justify-start gap-1">
          <Button
            :disabled="loading"
            @click="doPayment"
            class="flex w-full flex-1 items-center justify-center gap-1 rounded-md border-sky-500 bg-sky-500 px-[7px] py-3"
            aria-label="Unlock Predictions"
          >
            <i v-if="loading" class="pi pi-spin pi-spinner text-white"></i>
            <UnLockIcon v-if="!loading" class="h-4 w-4" />
            <div class="justify-start text-sm font-semibold text-white">Unlock Predictions</div>
          </Button>
          <div
            class="w-full justify-start text-center text-[11px] font-normal leading-normal text-slate-500"
          >
            (Limited-time offer. Cancel Anytime. Don't miss out!)
          </div>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
